import { apply, burst, celebrate, give, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { dayId } from '../core/state';
import { m } from '../i18n';
import { npcName, objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say, type Choice } from '../ui/dialog';
import { openArcade } from '../ui/arcade';
import { openStory } from '../ui/stories';
import { openMinigame } from '../minigames/registry';
import type { ObjectId } from '../world/mapdef';
import { logOrder, stamp } from './passport';

type Stall = 'mkTea' | 'mkChicken' | 'mkBalls' | 'mkTofu' | 'mkOyster' | 'mkIce';
type GameKey = Parameters<typeof openMinigame>[0];

const STALL_ICON: Record<Stall, string> = { mkTea: '🧋', mkChicken: '🍗', mkBalls: '🍠', mkTofu: '🍢', mkOyster: '🍳', mkIce: '🍧' };
const STALL_FX: Record<Stall, { energy: number; mood: number }> = {
  mkTea: { energy: 8, mood: 10 },
  mkChicken: { energy: 25, mood: 8 },
  mkBalls: { energy: 12, mood: 10 },
  mkTofu: { energy: 18, mood: 8 },
  mkOyster: { energy: 22, mood: 8 },
  mkIce: { energy: 6, mood: 16 },
};
const GAME_PRICE = 15;

const shuffle = <T>(a: T[]): T[] => a.map((v) => [Math.random(), v] as const).sort((x, y) => x[0] - y[0]).map((p) => p[1]);
const pick = <T>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)];

/** Order food in English: pick the polite sentence, then pay. */
function openStall(id: Stall): void {
  const t = m().world3.market;
  const st = t.stalls[id];
  const who = objName(id);
  sayEnglish(st.vendor, 0.9);
  const opts = shuffle([st.ok, ...st.bad]);
  const choices: Choice[] = opts.map((o) => ({
    text: `🗣️ ${o}`,
    cost: t.cost(st.price),
    run: () => {
      sayEnglish(o, 0.9);
      if (o !== st.ok) {
        sfx('bad');
        say({ who, icon: STALL_ICON[id], text: pick(t.wrong), choices: [{ text: m().common.back, run: () => openStall(id) }] });
        return;
      }
      const s = S();
      if (s.coins < st.price) return toast(t.poor);
      sfx('ok');
      burst('sparkle', 6);
      apply({ coins: -st.price, ...STALL_FX[id], minutes: 10 });
      logOrder();
      // The first polite order of the day earns a little extra.
      const extra = onceToday(`order.${id}`);
      if (extra) apply({ stars: 2 }, { float: false });
      say({ who, icon: STALL_ICON[id], text: extra ? `${st.done}\n\n${t.extra}` : st.done });
    },
  }));
  choices.push({ text: t.look });
  say({ who, icon: STALL_ICON[id], text: `“${st.vendor}”\n\n${t.orderPrompt}`, choices });
}

function booth(game: GameKey, label: ObjectId, text: string): void {
  const t = m().world3.market;
  say({
    who: objName(label),
    icon: '🎪',
    text,
    choices: [
      {
        text: t.playFor(GAME_PRICE),
        run: () => {
          if (S().coins < GAME_PRICE) return toast(t.poor);
          apply({ coins: -GAME_PRICE }, { float: false });
          sfx('pop');
          openMinigame(game);
        },
      },
      { text: t.look },
    ],
  });
}

const grandmaHome = () => S().player.map === 'grandma';

export const WORLD3_OBJECTS: Partial<Record<ObjectId, () => void>> = {
  // --- Grandma Lee's house ---------------------------------------------------
  grTea() {
    const t = m().world3.grandma;
    say({
      who: objName('grTea'),
      icon: '🍵',
      text: t.teaText,
      choices: [
        {
          text: t.drink,
          cost: m().common.minutes(20),
          run: () => {
            if (!onceToday('grTea')) return toast(m().common.alreadyToday);
            sfx('brew');
            apply({ mood: 12, energy: 6, minutes: 20 });
            S().affection.mom = Math.min(100, S().affection.mom + 1);
            say({ who: npcName('grandma'), icon: '👵', text: `${t.teaDone}\n\n${t.teaWords}` });
            sayEnglish('This tea smells lovely. Thank you, Grandma!', 0.85);
          },
        },
        { text: m().common.back },
      ],
    });
  },
  grCooker() {
    const t = m().world3.grandma;
    say({
      who: objName('grCooker'),
      icon: '🍚',
      text: t.cooker,
      choices: [
        {
          text: t.steam,
          cost: m().common.minutes(40),
          run: () => {
            if (!onceToday('grCooker')) return toast(m().common.alreadyToday);
            sfx('brew');
            burst('heart', 4);
            apply({ energy: 20, mood: 8, minutes: 40, stars: 3 });
            say({ who: npcName('grandma'), icon: '🥟', text: t.steamDone });
          },
        },
        { text: m().common.back },
      ],
    });
  },
  grPhotos() {
    const t = m().world3.grandma;
    say({ who: objName('grPhotos'), icon: '🖼️', text: t.photos, choices: [{ text: t.readStory, run: () => openStory('grandma') }, { text: m().common.back }] });
  },
  grSewing() {
    const t = m().world3.grandma;
    const learned = !!S().forever.sewing;
    say({
      who: objName('grSewing'),
      icon: '🧵',
      text: t.sewing,
      choices: learned
        ? [{ text: m().common.ok }]
        : [
            {
              text: t.sewLearn,
              cost: m().common.minutes(30),
              run: () => {
                S().forever.sewing = true;
                celebrate();
                give('scarf', 1);
                apply({ mood: 10, minutes: 30, stars: 5 });
                say({ who: npcName('grandma'), icon: '🧣', text: t.sewDone });
              },
            },
            { text: m().common.back },
          ],
    });
  },
  grCalendar() {
    const t = m().world3.grandma;
    const s = S();
    const date = `${m().hud.week(s.week)} · ${m().days[dayId(s)].name}`;
    say({
      who: objName('grCalendar'),
      icon: '📅',
      text: t.calendar(date),
      choices: [
        {
          text: t.tearPage,
          run: () => {
            if (!onceToday('calendar')) return toast(m().common.alreadyToday);
            sfx('rustle');
            apply({ mood: 4 });
            toast(t.tearDone);
          },
        },
        { text: m().common.back },
      ],
    });
  },
  // --- Bo's home -------------------------------------------------------------------
  boConsole() {
    const t = m().world3.bo;
    say({ who: objName('boConsole'), icon: '🎮', text: t.console, choices: [{ text: t.playConsole, run: openArcade }, { text: m().common.back }] });
  },
  boAquarium() {
    const t = m().world3.bo;
    say({
      who: objName('boAquarium'),
      icon: '🐠',
      text: t.aquarium,
      choices: [
        {
          text: t.feedFish,
          run: () => {
            if (!onceToday('boFish')) return toast(m().common.alreadyToday);
            sfx('water');
            apply({ mood: 6 });
            toast(t.fed);
          },
        },
        { text: m().common.back },
      ],
    });
  },
  boComics() {
    const t = m().world3.bo;
    say({ who: objName('boComics'), icon: '📚', text: t.comics, choices: [{ text: t.readComic, run: () => openStory('comic') }, { text: m().common.back }] });
  },
  boLego() {
    const t = m().world3.bo;
    say({
      who: objName('boLego'),
      icon: '🧱',
      text: t.lego,
      choices: [
        {
          text: t.build,
          cost: m().common.minutes(30),
          run: () => {
            if (!onceToday('boLego')) return toast(m().common.alreadyToday);
            celebrate();
            apply({ mood: 12, minutes: 30, stars: 3 });
            say({ who: npcName('bo'), icon: '🚀', text: t.built });
          },
        },
        { text: m().common.back },
      ],
    });
  },
  // --- Night market -------------------------------------------------------------------
  mkTea: () => openStall('mkTea'),
  mkChicken: () => openStall('mkChicken'),
  mkBalls: () => openStall('mkBalls'),
  mkTofu: () => openStall('mkTofu'),
  mkOyster: () => openStall('mkOyster'),
  mkIce: () => openStall('mkIce'),
  mkFish: () => booth('goldfish' as GameKey, 'mkFish', m().world3.market.gamesText),
  mkRings: () => booth('ringToss' as GameKey, 'mkRings', m().world3.market.gamesText),
  mkClaw: () => booth('claw' as GameKey, 'mkClaw', m().world3.market.clawText),
};

/** People in the new places. Returns false if the id isn't one of them. */
export function talkWorld3(id: string): boolean {
  const t = m().world3;
  switch (id) {
    case 'boMom':
      say({
        who: npcName('boMom'),
        icon: '👩',
        text: t.bo.momHello,
        choices: [
          {
            text: t.bo.momDumplings,
            run: () => {
              if (!onceToday('boMom')) return toast(m().common.alreadyToday);
              sayEnglish('Yes, please. They smell great!', 0.9);
              apply({ energy: 18, mood: 8 });
              say({ who: npcName('boMom'), icon: '🥟', text: t.bo.momDone });
            },
          },
          { text: m().common.back },
        ],
      });
      return true;
    case 'teaVendor':
      openStall('mkTea');
      return true;
    case 'chickenVendor':
      openStall('mkChicken');
      return true;
    case 'gameVendor':
      say({
        who: npcName('gameVendor'),
        icon: '🎪',
        text: t.market.gamesText,
        choices: [
          { text: `🐟 ${objName('mkFish')}`, run: () => WORLD3_OBJECTS.mkFish!() },
          { text: `🎯 ${objName('mkRings')}`, run: () => WORLD3_OBJECTS.mkRings!() },
          { text: `🧸 ${objName('mkClaw')}`, run: () => WORLD3_OBJECTS.mkClaw!() },
          { text: t.market.look },
        ],
      });
      return true;
    case 'shopper1':
    case 'shopper2':
    case 'shopper3':
      say({ who: npcName(id), icon: '🙂', text: pick(t.market.crowd) });
      return true;
    default:
      return false;
  }
}

/** Whether a door to one of the new places is open right now (else a reason). */
export function doorClosed(to: string): string | null {
  const s = S();
  const t = m().world3.closed;
  if (to === 'market') return s.time >= 17 * 60 && s.time < 23 * 60 ? null : t.market;
  if (to === 'grandma') return s.time >= 8 * 60 && s.time < 21 * 60 ? null : t.grandma;
  if (to === 'bo') return s.time >= 16 * 60 && s.time < 21 * 60 ? null : t.bo;
  return null;
}

/** First visit to the night market: welcome and stamp. */
export function enteredMap(id: string): void {
  if (id === 'market' && stamp('nightMarket')) toast(m().world3.market.welcome);
  void grandmaHome;
}
